import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import toast from 'react-hot-toast';
import API_CONFIG from '../config/api';

// Configurar axios para usar la URL base del servidor WAMP64
axios.defaults.baseURL = API_CONFIG.getBaseURL();
axios.defaults.headers.common['Content-Type'] = 'application/json';

const AuthContext = createContext();

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Configurar axios para incluir el token en todas las peticiones
  useEffect(() => {
    const token = Cookies.get('auth_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      // Verificar si el token es válido
      verifyToken(token);
    } else {
      setLoading(false);
    }
  }, []);

  const verifyToken = async (token) => {
    try {
      const response = await axios.post('/api/auth/verify.php', {}, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Error verificando token:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      setLoading(true);
      const response = await axios.post('/api/auth/login.php', {
        username,
        password
      });

      if (response.data.success) {
        const { token, user } = response.data.data;
        
        // Guardar token en cookie segura
        Cookies.set('auth_token', token, { 
          expires: 1, // 1 día
          secure: process.env.NODE_ENV === 'production',
          sameSite: 'strict'
        });
        
        // Configurar axios
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        
        setUser(user);
        setIsAuthenticated(true);
        
        toast.success('¡Bienvenido de vuelta!');
        return { success: true };
      } else {
        toast.error(response.data.message || 'Credenciales incorrectas');
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Error en login:', error);
      const message = error.response?.data?.message || 'Error de conexión';
      toast.error(message);
      return { success: false, message };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    // Remover token de cookies
    Cookies.remove('auth_token');
    
    // Limpiar header de axios
    delete axios.defaults.headers.common['Authorization'];
    
    // Limpiar estado
    setUser(null);
    setIsAuthenticated(false);
    
    toast.success('Sesión cerrada correctamente');
  };

  const value = {
    user,
    login,
    logout,
    loading,
    isAuthenticated
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}